import { Show, For } from "solid-js";
import { RoleBadge } from "../RoleBadge";
import { StatusBadge } from "../StatusBadge";
import { UserInfoCard } from "./UserInfoCard";
import { DropdownMenu } from "../DropdownMenu";

function providerColor(name: string) {
  switch (name) {
    case "anonymous": return "bg-amber-900/40 text-amber-300 border border-amber-700";
    case "google": return "bg-purple-900/40 text-purple-300 border border-purple-700";
    case "email": return "bg-cyan-900/40 text-cyan-300 border border-cyan-700";
    default: return "bg-gray-800 text-gray-400 border border-gray-700";
  }
}

interface UserRowProps {
  user: any;
  memberRole?: string;
  memberStatus?: string;
  isOwner: boolean;
  isExpanded: boolean;
  isEditing: boolean;
  editRole: string;
  editMemberStatus: string;
  editUserStatus: string;
  providers: string[];
  authLabel: string;
  onExpand: () => void;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onEditRoleChange: (value: string) => void;
  onEditMemberStatusChange: (value: string) => void;
  onEditUserStatusChange: (value: string) => void;
  onLoginLink: () => void;
  onSetPw: () => void;
  onVerifyEmail: () => void;
  onVerifyEmailDirect: () => void;
  onUnverifyEmail: () => void;
  onRemove: () => void;
}

export function UserRow(props: UserRowProps) {
  const providers = () => props.providers ?? [];
  return (
    <>
      <tr class="hover:bg-gray-900/50 cursor-pointer" onClick={props.onExpand}>
        <td class="px-4 py-3 text-gray-200">
          <span>{props.user.email}</span>
          <span class="ml-2 space-x-1">
            <For each={providers()}>{(p) => (
              <span class={`px-1.5 py-0.5 rounded text-[10px] font-medium ${providerColor(p)}`}>{p}</span>
            )}</For>
          </span>
        </td>
        <td class="px-4 py-3 text-gray-300">{props.user.displayName ?? "—"}</td>
        <td class="px-4 py-3 text-xs">
          <Show
            when={props.user.emailVerified}
            fallback={<span class="rounded border border-amber-700 bg-amber-900/40 px-2 py-0.5 text-amber-300">Unverified</span>}
          >
            <span class="rounded border border-emerald-700 bg-emerald-900/40 px-2 py-0.5 text-emerald-300">Verified</span>
          </Show>
        </td>
        <td class="px-4 py-3">
          <Show when={!props.isEditing} fallback={
            <select value={props.editRole} onChange={(e) => props.onEditRoleChange(e.currentTarget.value)} class="bg-gray-800 border border-cyan-600 rounded px-2 py-0.5 text-xs text-gray-200 cursor-pointer">
              <option value="" disabled>—</option>
              <option value="owner">Owner</option>
              <option value="manager">Manager</option>
              <option value="searcher">Searcher</option>
            </select>
          }>
            <RoleBadge role={props.memberRole ?? "searcher"} />
          </Show>
        </td>
        <td class="px-4 py-3">
          <Show when={!props.isEditing} fallback={
            <select value={props.editMemberStatus} onChange={(e) => props.onEditMemberStatusChange(e.currentTarget.value)} class="bg-gray-800 border border-cyan-600 rounded px-2 py-0.5 text-xs text-gray-200 cursor-pointer">
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
            </select>
          }>
            <Show when={props.memberStatus} fallback={<span class="text-gray-600 text-xs">—</span>}>
              <StatusBadge status={props.memberStatus!} />
            </Show>
          </Show>
        </td>
        <td class="px-4 py-3">
          <Show when={!props.isEditing} fallback={
            <select value={props.editUserStatus} onChange={(e) => props.onEditUserStatusChange(e.currentTarget.value)} class="bg-gray-800 border border-cyan-600 rounded px-2 py-0.5 text-xs text-gray-200 cursor-pointer">
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
            </select>
          }>
            <StatusBadge status={props.user.status} />
          </Show>
        </td>
        <td class="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
          <Show when={!props.isEditing} fallback={
            <>
              <button title="Save user and membership changes" onClick={props.onSave} class="text-xs px-2 py-0.5 bg-emerald-900/30 border border-emerald-700 text-emerald-300 rounded hover:bg-emerald-900/50 cursor-pointer">Save</button>
              <button title="Discard edits for this user" onClick={props.onCancel} class="text-xs px-2 py-0.5 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 cursor-pointer ml-1">Cancel</button>
            </>
          }>
            <DropdownMenu label="Actions">
              <button title="Edit organization role and user status" onClick={() => { props.onStartEdit(); }} class="w-full text-left px-3 py-2 text-xs text-gray-200 hover:bg-gray-700 cursor-pointer">Edit</button>
              <button title="Generate a one-time login link" onClick={() => { props.onLoginLink(); }} class="w-full text-left px-3 py-2 text-xs text-gray-200 hover:bg-gray-700 cursor-pointer">Login Link</button>
              <button title="Reset this user's password" onClick={() => { props.onSetPw(); }} class="w-full text-left px-3 py-2 text-xs text-gray-200 hover:bg-gray-700 cursor-pointer">Set Password</button>
              <Show when={!props.user.emailVerified}>
                <button title="Mark this user's email as verified" onClick={() => { props.onVerifyEmailDirect(); }} class="w-full text-left px-3 py-2 text-xs text-gray-200 hover:bg-gray-700 cursor-pointer">Verify Email</button>
              </Show>
              <Show when={props.user.emailVerified}>
                <button title="Mark this user's email as unverified" onClick={() => { props.onUnverifyEmail(); }} class="w-full text-left px-3 py-2 text-xs text-gray-200 hover:bg-gray-700 cursor-pointer">Unverify Email</button>
              </Show>
              <Show when={!props.user.emailVerified}>
                <button title="Generate an email verification link" onClick={() => { props.onVerifyEmail(); }} class="w-full text-left px-3 py-2 text-xs text-gray-200 hover:bg-gray-700 cursor-pointer">Generate Verification Link</button>
              </Show>
              <Show when={!props.isOwner}>
                <button title="Remove this user from the organization" onClick={() => { props.onRemove(); }} class="w-full text-left px-3 py-2 text-xs text-red-300 hover:bg-gray-700 cursor-pointer border-t border-gray-700">Remove from Org</button>
              </Show>
            </DropdownMenu>
          </Show>
        </td>
      </tr>
      <Show when={props.isExpanded}>
        <UserInfoCard
          user={props.user}
          memberStatus={props.memberStatus}
          providers={props.providers}
        />
      </Show>
    </>
  );
}
